import { useIntl } from 'react-intl';

import LabeledValue from '#components/form/LabeledValue.tsx';
import type { BulkMaterial } from '#domain/BulkMaterial.ts';
import { hasDeviation } from '#domain/hasDeviation.ts';
import { unitOptions } from '#features/orderForm/model/unitOptions.ts';

import { DeviationValue } from './DeviationValue.tsx';

type BulkDetailsProps = {
    material: BulkMaterial;
};

const sectionClasses = 'display-flex flex-column gap-10 bg-lightest padding-10 rounded';
const columnClasses = 'display-grid gap-10';

export const BulkMaterialDetails = ({ material }: BulkDetailsProps) => {
    const intl = useIntl();

    const hasLoadingDeviation = hasDeviation(material.quantity.target, material.quantity.loaded);
    const hasUnloadingDeviation = hasDeviation(material.quantity.target, material.quantity.unloaded);

    const emptyPlaceholder = '-';
    const loadedQuantity = material.quantity.loaded;
    const unloadedQuantity = material.quantity.unloaded;

    const unitLabel = unitOptions.find(u => u.id === material.unitType)?.label ?? '';

    return (
        <div data-testid={`order-stop-form-${material.id}`} className={sectionClasses}>
            <div className={`${columnClasses} grid-cols-1`}>
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.cargo',
                    })}
                    value={material.name}
                    className=''
                />
            </div>
            <div className={`${columnClasses} grid-cols-3`}>
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.target-quantity',
                    })}
                    value={`${material.quantity.target} ${unitLabel}`}
                />
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.quantity-loaded',
                    })}
                    value={
                        hasLoadingDeviation ? (
                            <DeviationValue
                                targetQuantity={material.quantity.target}
                                actualQuantity={loadedQuantity}
                                emptyPlaceholder={emptyPlaceholder}
                                unit={unitLabel}
                            />
                        ) : loadedQuantity !== undefined ? (
                            `${loadedQuantity} ${unitLabel}`
                        ) : (
                            emptyPlaceholder
                        )
                    }
                />
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.quantity-unloaded',
                    })}
                    value={
                        hasUnloadingDeviation ? (
                            <DeviationValue
                                targetQuantity={material.quantity.target}
                                actualQuantity={unloadedQuantity}
                                emptyPlaceholder={emptyPlaceholder}
                                unit={unitLabel}
                            />
                        ) : unloadedQuantity !== undefined ? (
                            `${unloadedQuantity} ${unitLabel}`
                        ) : (
                            emptyPlaceholder
                        )
                    }
                />
            </div>
            {material.comment && (
                <div className={`${columnClasses} grid-cols-1`}>
                    {material.comment.loading && (
                        <LabeledValue
                            label={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.comment.driver.loading',
                            })}
                            className='padding-x-15 padding-y-5'
                            icon='comment'
                            value={material.comment.loading}
                        />
                    )}
                    {material.comment.unloading && (
                        <LabeledValue
                            label={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.comment.driver.unloading',
                            })}
                            className='padding-x-15 padding-y-5'
                            icon='comment'
                            value={material.comment.unloading}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
